import React, { useContext, useEffect, useState } from 'react';

import { buildRfcThreadingForOpenMessage } from './emailThreadingHeaders';

/* global Office */

/**
 * Max wait for optional Office.js async APIs whose callbacks may never run on some Outlook builds,
 * so task pane context initialization cannot hang indefinitely.
 */
const OFFICE_ASYNC_API_TIMEOUT_MS = 2_000;

export const INITIAL_OFFICE_CONTEXT: IOfficeContext = {
  isLoading: true,
  isInitialized: false,
  inboxEmailAddress: '',
  subject: '',
  threadId: '',
  threadItemId: '',
  isOutlookReply: false,
  isSharedMailboxEmail: false,
  threadReferences: '',
  inReplyTo: [],
};

interface OfficeContextProviderProps {
  children: React.ReactNode;
}

export const OfficeContext = React.createContext<IOfficeContext>(
  INITIAL_OFFICE_CONTEXT
);

const getNormalizedItemId = (itemId?: string): string => {
  if (!itemId) {
    return '';
  }

  const mailbox = Office?.context?.mailbox;
  const convertToRestId = mailbox?.convertToRestId;

  if (!convertToRestId || !Office?.MailboxEnums?.RestVersion) {
    return itemId;
  }

  try {
    const convertedId = convertToRestId(
      itemId,
      Office.MailboxEnums.RestVersion.v2_0
    );

    if (convertedId) {
      return convertedId;
    }
  } catch {
    // Swallow errors and fall back to the original ID; some contexts (e.g., Compose)
    // may block convertToRestId even when the function exists.
  }

  return itemId;
};

/**
 * Reads the RFC `References` internet header for the given mail item (read mode).
 * Used with {@link buildRfcThreadingForOpenMessage} for portal bid notification threading.
 *
 * Resolves to `''` when the API is missing, the request fails, the header is absent, or the
 * callback never fires within {@link OFFICE_ASYNC_API_TIMEOUT_MS} (some hosts never invoke it).
 *
 * @param item - Office item in read mode; may expose `getInternetHeadersAsync` on supported builds.
 * @returns The References header value, or `''`.
 */
const fetchReferencesHeaderAsync = (item: Office.Item): Promise<string> => {
  const withHeaders = item as unknown as {
    getInternetHeadersAsync?: (
      headerNames: string[],
      callback: (result: Office.AsyncResult<unknown>) => void
    ) => void;
  };

  const getInternetHeadersAsync = withHeaders.getInternetHeadersAsync;
  if (typeof getInternetHeadersAsync !== 'function') {
    return Promise.resolve('');
  }

  return new Promise((resolve) => {
    let settled = false;
    const timeoutId = setTimeout(() => {
      if (settled) return;
      settled = true;
      console.warn(
        '[Drumkit] getInternetHeadersAsync timed out, continuing without References header'
      );
      resolve('');
    }, OFFICE_ASYNC_API_TIMEOUT_MS);

    getInternetHeadersAsync.call(item, ['references'], (result) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      if (
        result.status !== Office.AsyncResultStatus.Succeeded ||
        !result.value
      ) {
        resolve('');
        return;
      }

      const value = result.value as Record<string, string>;
      const match = Object.entries(value).find(
        ([key]) => key.toLowerCase() === 'references'
      );
      const refs = match?.[1];
      resolve(typeof refs === 'string' ? refs : '');
    });
  });
};

export const useOfficeContext = () => useContext(OfficeContext);

export const OfficeContextProvider: React.FC<OfficeContextProviderProps> = ({
  children,
}) => {
  const [state, setState] = useState<IOfficeContext>(INITIAL_OFFICE_CONTEXT);

  useEffect(() => {
    const asyncHandler = async () => {
      // Helper to fetch recipients asynchronously (for compose mode)
      const getRecipientsAsync = (
        recipientsObj: Office.Recipients
      ): Promise<EmailAddressDetails[]> => {
        return new Promise((resolve) => {
          recipientsObj.getAsync((result) => {
            if (result.status === Office.AsyncResultStatus.Succeeded) {
              const recipients = result.value.map(
                (r: { displayName: string; emailAddress: string }) => ({
                  displayName: r.displayName,
                  emailAddress: r.emailAddress,
                })
              );
              resolve(recipients);
            } else {
              console.warn('[Drumkit] Failed to get recipients:', result.error);
              resolve([]);
            }
          });
        });
      };

      // Helper to fetch from address asynchronously (for compose mode)
      const getFromAsync = (
        fromObj: Office.From
      ): Promise<EmailAddressDetails | null> => {
        return new Promise((resolve) => {
          fromObj.getAsync((result) => {
            if (result.status === Office.AsyncResultStatus.Succeeded) {
              const from = result.value;
              resolve({
                displayName: from.displayName || '',
                emailAddress: from.emailAddress || '',
              });
            } else {
              console.warn('[Drumkit] Failed to get from:', result.error);
              resolve(null);
            }
          });
        });
      };

      // Helper to detect if the current item is in a shared folder/mailbox (Office.js 1.8/1.13).
      // Success = shared context; failure or missing API = not shared.
      // Uses OFFICE_ASYNC_API_TIMEOUT_MS timeout so affected Outlook clients that never fire the callback don't leave the context loading.

      const getIsSharedMailboxEmail = (
        item: Office.Item | undefined
      ): Promise<boolean> => {
        if (
          !item ||
          typeof (item as any).getSharedPropertiesAsync !== 'function'
        ) {
          return Promise.resolve(false);
        }
        return new Promise((resolve) => {
          let settled = false;
          const timeoutId = setTimeout(() => {
            if (settled) return;
            settled = true;
            console.warn(
              '[Drumkit] getSharedPropertiesAsync timed out; assuming not shared mailbox'
            );
            resolve(false);
          }, OFFICE_ASYNC_API_TIMEOUT_MS);

          (item as any).getSharedPropertiesAsync(
            (result: Office.AsyncResult<Office.SharedProperties>) => {
              if (settled) return;
              settled = true;
              clearTimeout(timeoutId);
              if (result.status === Office.AsyncResultStatus.Succeeded) {
                resolve(true);
              } else {
                resolve(false);
              }
            }
          );
        });
      };

      // We'll call this function to read the current office state
      // from mailbox, item.
      const update = async () => {
        // Unpack the office context.
        const mailbox = Office.context.mailbox;
        const item = mailbox?.item;

        const getItemState = async (): Promise<IItemState | undefined> => {
          if (!item) {
            return undefined;
          }

          const {
            from,
            to,
            internetMessageId,
            conversationId,
            itemType,
            cc,
            body,
            itemId,
            subject,
            dateTimeCreated,
          } = item;

          // Check if we're in compose mode (to/cc are Recipients objects, not arrays)
          const isComposeMode = !Array.isArray(to);

          let resolvedFrom: EmailAddressDetails = from as EmailAddressDetails;
          let resolvedTo: EmailAddressDetails[] = to as EmailAddressDetails[];
          let resolvedCc: EmailAddressDetails[] = cc as EmailAddressDetails[];

          if (isComposeMode) {
            // In compose mode, fetch recipients asynchronously
            const toRecipients = to as unknown as Office.Recipients;
            const ccRecipients = cc as unknown as Office.Recipients;
            const fromObj = from as unknown as Office.From;

            const [fetchedTo, fetchedCc, fetchedFrom] = await Promise.all([
              toRecipients
                ? getRecipientsAsync(toRecipients)
                : Promise.resolve([]),
              ccRecipients
                ? getRecipientsAsync(ccRecipients)
                : Promise.resolve([]),
              fromObj ? getFromAsync(fromObj) : Promise.resolve(null),
            ]);

            resolvedTo = fetchedTo;
            resolvedCc = fetchedCc;
            resolvedFrom = fetchedFrom || { displayName: '', emailAddress: '' };
          }

          return {
            from: resolvedFrom,
            to: resolvedTo,
            cc: resolvedCc,
            internetMessageId,
            conversationId,
            itemType,
            itemId,
            body,
            subject,
            dateTimeCreated,
          };
        };

        const getMailboxState = async (): Promise<
          IMailboxState | undefined
        > => {
          if (!mailbox) {
            return undefined;
          }

          const { userProfile } = mailbox;

          return {
            userProfile,
            item: await getItemState(),
          };
        };

        const mailboxState = await getMailboxState();

        const officeItem = mailbox?.item;
        const rawMessageId =
          (officeItem as unknown as { internetMessageId?: string })
            ?.internetMessageId ?? '';
        const needsThreadingHeaders =
          !!officeItem &&
          Array.isArray(officeItem.to) &&
          rawMessageId.trim().length > 0;

        const [isSharedMailboxEmail, referencesHeader] = await Promise.all([
          getIsSharedMailboxEmail(mailbox?.item),
          needsThreadingHeaders
            ? fetchReferencesHeaderAsync(officeItem)
            : Promise.resolve(''),
        ]);

        let threadReferences = '';
        let inReplyTo: string[] = [];
        if (needsThreadingHeaders) {
          const threading = buildRfcThreadingForOpenMessage(
            rawMessageId,
            referencesHeader
          );
          threadReferences = threading.threadReferences;
          inReplyTo = threading.inReplyTo;
        }

        setState({
          state: {
            mailbox: mailboxState,
          },
          isLoading: false,
          isInitialized: true,
          inboxEmailAddress: mailbox.userProfile.emailAddress,
          subject: item?.subject ?? '',
          threadId: mailbox.item?.conversationId ?? '',
          threadItemId: getNormalizedItemId(mailbox.item?.itemId),
          // TODO improve logic
          // if drumkit is open on outlook but doesn't have item.from as a sender,
          // we can assume we're not reading but replying instead
          isOutlookReply: !!(
            mailbox?.item?.from && !mailbox.item.from.emailAddress
          ),
          isSharedMailboxEmail,
          threadReferences,
          inReplyTo,
        });
      };

      // Update our application, now that Office is initialized.
      await update();
      // update data when mail item changes, for pinnable task pane
      Office.context.mailbox &&
        Office.context.mailbox.addHandlerAsync(
          Office.EventType.ItemChanged,
          update
        );
    };
    Office.onReady(() => {
      asyncHandler();
    });
  }, []);

  return (
    <OfficeContext.Provider value={state}>{children}</OfficeContext.Provider>
  );
};

export const OfficeContextConsumer = OfficeContext.Consumer;

export type EmailAddressDetails = {
  displayName: string;
  emailAddress: string;
};

export interface IItemState {
  from: EmailAddressDetails;
  to: EmailAddressDetails[];
  cc: EmailAddressDetails[];
  internetMessageId: string;
  conversationId: string;
  itemType: string;
  itemId: string;
  body: Office.Body;
  subject: string;
  dateTimeCreated: Date;
}

export interface IMailboxState {
  item?: IItemState;
  userProfile: EmailAddressDetails;
}

export interface IOfficeState {
  mailbox?: IMailboxState;
}

export interface IOfficeContext {
  // Different from isInitialized because we don't want to show the "Please open add-in in Outlook" message while
  // OfficeContext is still loading
  isLoading: boolean;
  isInitialized: boolean;
  inboxEmailAddress: string;
  subject: string;
  threadId: string;
  threadItemId: string;
  isOutlookReply: boolean;
  isSharedMailboxEmail: boolean;
  // RFC References chain for portal bid notification threading (read mode).
  threadReferences: string;
  // RFC In-Reply-To ids for portal bid notification threading (read mode).
  inReplyTo: string[];
  state?: IOfficeState;
}
